import { applyDocumentTheme, readDocumentTheme, type ThemeName } from "@strust/ui";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

// spec explorer-access / Display theme (RF-ACC-11) and explorer-design-system / No flash of the
// wrong theme (RNF-DS-03): `/theme-init.js` set `<html data-theme>` before the first paint; this
// provider keeps it in React state, applies changes without a reload and prints in light.

interface ThemeContextValue {
  readonly theme: ThemeName;
  readonly setTheme: (theme: ThemeName) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext);
  if (!value) throw new Error("useTheme must be used inside <ThemeProvider>");
  return value;
}

export function ThemeProvider({ children }: { readonly children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemeName>(readDocumentTheme);

  const setTheme = useCallback((next: ThemeName) => {
    applyDocumentTheme(next);
    setThemeState(next);
  }, []);

  // Printouts (transaction detail, profile) use the light palette whatever the screen theme.
  useEffect(() => {
    const before = () => {
      document.documentElement.setAttribute("data-theme", "light");
    };
    const after = () => {
      document.documentElement.setAttribute("data-theme", theme);
    };
    window.addEventListener("beforeprint", before);
    window.addEventListener("afterprint", after);
    return () => {
      window.removeEventListener("beforeprint", before);
      window.removeEventListener("afterprint", after);
    };
  }, [theme]);

  const value = useMemo(() => ({ theme, setTheme }), [theme, setTheme]);
  return <ThemeContext value={value}>{children}</ThemeContext>;
}
